import { Sun, Moon, MonitorSmartphone } from "lucide-react";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/DefaultSelectDropdown";
import ContextMenu, { ContextMenuItem } from "@/components/ui/ContextMenu";
import { isDevelopmentMode } from "@/lib/utils/devRoleOverride";
import { useTheme } from "@/providers/ThemeProvider";

interface ThemeSwitcherProps {
  collapsed?: boolean;
}

export default function ThemeSwitcher({ collapsed = false }: ThemeSwitcherProps) {
  // Don't render if not in development mode
  if (!isDevelopmentMode()) {
    return null;
  }

  const { theme, effectiveTheme, setTheme } = useTheme();
  const [contextMenu, setContextMenu] = useState<{ isOpen: boolean; position: { x: number; y: number } }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });

  const handleThemeChange = (newTheme: string) => {
    console.log('🎨 [ThemeSwitcher] Switching to theme:', newTheme);
    setTheme(newTheme as 'light' | 'dark' | 'auto');
  };

  const getThemeIcon = () => {
    if (theme === 'auto') {
      return MonitorSmartphone;
    }
    return effectiveTheme === 'dark' ? Moon : Sun;
  };

  const getThemeLabel = (themeValue: string) => {
    const labels = {
      light: '☀️ Light',
      dark: '🌙 Dark',
      auto: '🖥️ Auto'
    };
    return labels[themeValue as keyof typeof labels] || themeValue;
  };

  if (collapsed) {
    const menuItems: ContextMenuItem[] = [
      {
        id: 'light',
        label: '☀️ Light',
        onClick: () => handleThemeChange('light'),
        disabled: theme === 'light',
      },
      {
        id: 'dark',
        label: '🌙 Dark',
        onClick: () => handleThemeChange('dark'),
        disabled: theme === 'dark',
      },
      {
        id: 'auto',
        label: '🖥️ Auto',
        onClick: () => handleThemeChange('auto'),
        disabled: theme === 'auto',
      },
    ];

    const ThemeIcon = getThemeIcon();

    return (
      <>
        <button
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setContextMenu({
              isOpen: true,
              position: {
                x: rect.right + 8,
                y: rect.top,
              },
            });
          }}
          className="flex items-center justify-center w-8 h-8 rounded-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          style={{ 
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)'
          }}
          title={`Dev Theme Switcher\nCurrent: ${getThemeLabel(theme)}\nEffective: ${effectiveTheme}\nClick to change theme`}
        >
          <ThemeIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'rgb(59, 130, 246)' }} />
        </button>
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          items={menuItems}
          onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        />
      </>
    );
  }

  const ThemeIcon = getThemeIcon();

  return (
    <div className="px-2 py-2">
      <Select
        value={theme}
        onValueChange={handleThemeChange}
      >
        <SelectTrigger 
          className="w-full"
          style={{ 
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.3)'
          }}
        >
          <div className="flex items-center gap-2 w-full">
            <ThemeIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'rgb(59, 130, 246)' }} />
            <SelectValue placeholder="Select Theme (Dev)" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="light">
            ☀️ Light
          </SelectItem>
          <SelectItem value="dark">
            🌙 Dark
          </SelectItem>
          <SelectItem value="auto">
            🖥️ Auto (System)
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400">
        🎨 Dev Theme: {effectiveTheme === 'dark' ? '🌙' : '☀️'} {effectiveTheme}
      </div>
    </div>
  );
}
